import {
    configureStore,
    type AnyAction,
    type Dispatch,
    type SerializedError,
    type ThunkDispatch,
} from '@reduxjs/toolkit';
import contextMenuReducer, { type IContextMenuState } from './ContextMenuSlice';
import apryseEventReducer, { type IApryseEventState } from './ApryseEventSlice';
import infoDialogReducer, { type IInfoDialogState } from './InfoDialogSlice';
import initSettingsReducer, { type IInitSettingsState } from './InitSettingsSlice';
import messageDialogReducer, { type IMessageDialogState } from './MessageDialogSlice';
import printDialogReducer, { type IPrintDialogState } from './PrintDialogSlice';
import selectedSidepanelReducer, { type ISelectedSidepanelState } from './SelectedSidepanelSlice';
import selectedStateReducer, { type ISelectedStateState } from './SelectedStateSlice';
import selectedViewModeReducer, { type IEplassState } from './EplassSlice';
import stampDialogReducer, { type IStampDialogState } from './StampDialogSlice';
import userSettingsReducer, { type IUserSettingsState } from './UserSettingsSlice';
import tooltipReducer, { type ITooltipState } from './TooltipSlice';
import searchReducer, { type ISearchState } from './SearchSlice';
import sessionReducer, { type ISessionState } from './SessionSlice';

export interface IGlobalStoreReducers {
    contextMenu: IContextMenuState;
    eplassState: IEplassState;
    apryseEvent: IApryseEventState;
    infoDialog: IInfoDialogState;
    initSettings: IInitSettingsState;
    messageDialog: IMessageDialogState;
    printDialog: IPrintDialogState;
    selectedSidepanel: ISelectedSidepanelState;
    selectedState: ISelectedStateState;
    stampDialog: IStampDialogState;
    tooltip: ITooltipState;
    userSettings: IUserSettingsState;
    searchState: ISearchState;
    sessionState: ISessionState;
}

/**
 * Interface that describes an action that is run against a slice's state object.
 */
export interface IAction<T> {
    /** Action type. */
    type: string;

    /** Action payload object. */
    payload: T;

    /** (Optional) Action error object. */
    error?: SerializedError;
}

/**
 * Interface that describes the base object properties of a redux state slice that sends requests to the backend.
 */
export interface SliceProps {
    /** Whether a resource is being loaded or not. */
    loading: boolean;

    /** Error that is thrown during resource fetching. */
    error: SerializedError | null;
}

/**
 * Interface that describes the base object properties of a redux state slice that requests backend data.
 */
export interface SlicePropsWithData<T> extends SliceProps {
    /** Data that is fetched from the backend. */
    data: T;
}

export const globalStore = configureStore({
    reducer: {
        contextMenu: contextMenuReducer,
        eplassState: selectedViewModeReducer,
        apryseEvent: apryseEventReducer,
        infoDialog: infoDialogReducer,
        initSettings: initSettingsReducer,
        messageDialog: messageDialogReducer,
        printDialog: printDialogReducer,
        selectedSidepanel: selectedSidepanelReducer,
        selectedState: selectedStateReducer,
        stampDialog: stampDialogReducer,
        tooltip: tooltipReducer,
        userSettings: userSettingsReducer,
        searchState: searchReducer,
        sessionState: sessionReducer,
    },
});
export type IGlobalStore = ReturnType<typeof globalStore.getState>;

export type AppDispatch = Dispatch<AnyAction> & ThunkDispatch<IGlobalStore, null, AnyAction>;

export default globalStore;
